import * as React from "react";
import { useState } from "react";
import { Box, Checkbox, Divider, Paper } from "@mui/material";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import AdjustOutlinedIcon from "@mui/icons-material/AdjustOutlined";
import { CheckboxCheckedIcon, CheckboxIcon } from "@/icons";
import Stack from "@mui/material/Stack";
import { GoMakeAutoComplate } from "./auto-complete";
import { useStyle } from "./style";
import { PrimaryButton } from "@/components/button/primary-button";

type MultiSelectOption = {
  value: string;
  label: string;
  isDivider?: boolean;
  isSectionHeader?: boolean;
  sectionIcon?: "type" | "status";
};

interface IProps {
  options: Array<MultiSelectOption>;
  values: string[];
  onChange: (values: string[]) => void;
  style?: React.CSSProperties;
  placeholder?: string;
  showApplyButton?: boolean;
  onClickApply?: () => void;
  applyText?: string;
  isClearable?: boolean;
  onClear?: () => void;
  preserveOptionOrder?: boolean;
}

const GoMakeMultiSelect = ({
  options,
  values,
  style,
  onChange,
  placeholder = "",
  showApplyButton = false,
  onClickApply,
  applyText = "Apply",
  isClearable = false,
  onClear,
  preserveOptionOrder = false,
}: IProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { classes } = useStyle();
  const selectableOptions =
    options?.filter(
      (option) => !option.isDivider && !option.isSectionHeader
    ) || [];

  const renderSectionIcon = (sectionIcon?: "type" | "status") => {
    const iconStyle = { fontSize: 16, color: "#6B7280" };

    if (sectionIcon === "status") {
      return <AdjustOutlinedIcon sx={iconStyle} />;
    }

    return <LocalOfferOutlinedIcon sx={iconStyle} />;
  };
  const reorderedOptions = preserveOptionOrder
    ? options
    : selectableOptions.sort((a, b) => {
      const isSelectedA = values.includes(a.value);
      const isSelectedB = values.includes(b.value);

      if (isSelectedA && !isSelectedB) {
        return -1;
      } else if (!isSelectedA && isSelectedB) {
        return 1;
      } else {
        return 0;
      }
    });

  const selectedLabels = values
    .map((selectedId) => options?.find((opt) => opt.value === selectedId)?.label)
    .filter((label) => label !== null && label !== undefined && label !== "");

  const handleSelectCheck = (isChecked: boolean, option: any) => {
    const updatedValues = isChecked
      ? [...values, option?.value]
      : values.filter((v) => v !== option?.value);
    onChange && onChange(updatedValues);
  };

  return (
    <GoMakeAutoComplate
      style={style || classes.multiSelectStyle}
      value={selectedLabels}
      disableClearable={!isClearable}
      placeholder={placeholder}
      options={reorderedOptions}
      withArrow={true}
      open={menuOpen}
      onOpen={() => setMenuOpen(true)}
      onClose={(_event: any, reason: string) => {
        if (reason === "selectOption") {
          setMenuOpen(true);
          return;
        }
        setMenuOpen(false);
      }}
      getOptionDisabled={(option: MultiSelectOption) =>
        Boolean(option.isDivider || option.isSectionHeader)
      }
      onChange={(_event: any, value: any) => {
        if (!value || (Array.isArray(value) && value.length === 0)) {
          onChange([]);
          onClear?.();
        }
      }}
      renderOption={(props: any, option: any) => {
        if (option.isSectionHeader) {
          return (
            <li
              {...props}
              key={option.value}
              style={{
                ...props.style,
                padding: "6px 8px 4px",
                pointerEvents: "none",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: "100%",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  backgroundColor: "#FFF",
                }}
              >
                {renderSectionIcon(option.sectionIcon)}
                <Box
                  component="span"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#6B7280",
                    letterSpacing: "0.4px",
                    textTransform: "uppercase",
                  }}
                >
                  {option.label}
                </Box>
              </Box>
            </li>
          );
        }
        const isChecked = values?.includes(option?.value);

        return (
          <li
            {...props}
            key={option.value}
            style={{
              ...props.style,
              cursor: "pointer",
              padding: "0px",
            }}
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleSelectCheck(!isChecked, option);
              setMenuOpen(true);
            }}
          >
            <Stack
              style={{
                ...classes.multiSelectOption,
                paddingInlineStart: "8px",
              }}
            >
              <Checkbox
                checked={isChecked}
                icon={<CheckboxIcon />}
                checkedIcon={<CheckboxCheckedIcon />}
                tabIndex={-1}
                disableRipple
                sx={{ pointerEvents: "none" }}
              />
              <div>{option.label}</div>
            </Stack>
          </li>
        );
      }}
      PaperComponent={(props: any) => (
        <Paper style={style || classes.multiSelectStyle} {...props}>
          {props?.children}
          {showApplyButton && (
            <div
              style={{
                padding: "8px",
                borderTop: "1px solid var(--theme-border-color, #e5e7eb)",
                backgroundColor: "var(--theme-bg-primary, #ffffff)",
              }}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <PrimaryButton
                variant="contained"
                style={{ width: "100%", height: 36 }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setMenuOpen(false);
                  onClickApply?.();
                }}
              >
                {applyText}
              </PrimaryButton>
            </div>
          )}
        </Paper>
      )}
    />
  );
};

export { GoMakeMultiSelect };
